/**
 * ELF-CIRCLE — Signature Work (design artboard `#6a`).
 *
 * Three work tiles whose detail unfolds on hover, then the portfolio category
 * rows.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import filmArt from '../../../../../assets/elf/portfolio-film.jpg';
import recordingArt from '../../../../../assets/elf/portfolio-recording.jpg';
import theatreArt from '../../../../../assets/elf/portfolio-theatre.jpg';

/** `tone` is the flat colour a tile turns to when opened, picked from its
    picture; `ink` is the text colour that reads on it. */
const WORK = [
  {
    art: recordingArt,
    tone: '#0b6fd8',
    ink: '#ffffff',
    kicker: '01 — Label & Independent Artist Rollouts',
    name: 'Recording Industry',
    body:
      'Direct work on both sides of the record industry: label-side campaigns for artist rollouts, and independent artist/manager-direct campaigns for those building outside the label system. Pre-release buzz, drop-day activation, playlist and press placement, and the media buy strategy that turns a release date into a cultural moment rather than a calendar entry.',
  },
  {
    art: filmArt,
    tone: '#eb9027',
    ink: '#0a0a12',
    kicker: '02 — Tribeca Film Festival, Coming Attractions',
    name: 'Film',
    body:
      'Marketing support engaged directly by individual film distributors and production companies whose titles premiere at Tribeca — building anticipation ahead of festival dates, press and premiere-night strategy, and audience-building for independent and studio titles alike.',
  },
  {
    art: theatreArt,
    tone: '#ffe135',
    ink: '#0a0a12',
    kicker: '03 — NYC',
    name: 'Off-Broadway Theatre',
    body:
      'Marketing and promotion for off-Broadway plays — another proof point for the firm’s range across live cultural programming, alongside music and film.',
  },
];

const CATEGORIES = [
  ['Music', 'Label-side artist rollouts, independent artist/manager campaigns'],
  ['Film', 'Tribeca Film Festival — distributor/production-direct, coming attractions'],
  ['Theatre', 'NYC off-Broadway marketing & promotion'],
  ['Live Events (Client)', 'Marketing for institutional halls — Lincoln Center, NJPAC, DIA'],
  ['Lifestyle & Fashion', 'To be built out alongside client wins'],
];

export const Portfolio: React.FC = () => {
  /** Tap equivalent of hover: the tile whose detail is unfolded. */
  const [openTile, setOpenTile] = useState<string | null>(null);
  const consult = useConsult();
  return (
  <ElfShell>
    <div className="elf-page">
      <div className="elf-page__panel">
        <ElfPageNav current="/portfolio" />
        <div style={{ padding: '18px var(--pgut) 0' }}>
          <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Portfolio']} />
        </div>

        <div
          className="elf-page__split"
          style={{
            padding: '40px var(--pgut) 26px',
            display: 'grid',
            gridTemplateColumns: '1fr 420px',
            gap: 56,
            alignItems: 'end',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <span className="eyebrow">
              <i />
              Signature work
            </span>
            <h1 className="elf-page__h1" style={{ fontSize: 'clamp(44px,4.8vw,74px)' }}>
              Music, film
              <br />&amp; <span style={{ color: 'var(--acc-panel)' }}>live production.</span>
            </h1>
          </div>
          <p className="wbody" style={{ fontSize: 16 }}>
            Flagship proof points, not line items — the campaign categories that carry cultural
            credibility hard to buy and easy to recognize.
          </p>
        </div>

        <div
          className="elf-page__grid3"
          style={{ padding: '14px var(--pgut) 40px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}
        >
          {WORK.map((w) => (
            <div
              className="worktile"
              key={w.name}
              data-open={openTile === w.name ? 'true' : undefined}
              style={{ '--tile': w.tone, '--tile-ink': w.ink } as React.CSSProperties}
              role="button"
              tabIndex={0}
              aria-expanded={openTile === w.name}
              onClick={() => setOpenTile((cur) => (cur === w.name ? null : w.name))}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                setOpenTile((cur) => (cur === w.name ? null : w.name));
              }}
            >
              <img src={w.art} alt="" />
              <span className="scrim" />
              <div className="wbodyx">
                <span className="worktile__kicker">{w.kicker}</span>
                <span className="worktile__name">{w.name}</span>
                <div className="more">
                  <p>{w.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '0 var(--pgut) 52px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="colhead" style={{ padding: '0 22px 10px' }}>
            Portfolio categories
          </span>
          {CATEGORIES.map(([name, desc]) => (
            <div className="catrow" key={name}>
              <span className="whead" style={{ fontSize: 20 }}>
                {name}
              </span>
              <span className="wbody" style={{ fontSize: 15 }}>
                {desc}
              </span>
            </div>
          ))}
          <p className="wtag" style={{ fontSize: 12.5, padding: '14px 22px 0', margin: 0 }}>
            Made In New Jersey Festival is treated separately as an ELF-CIRCLE-owned property, not
            client portfolio work.
          </p>
        </div>

        <div
          className="elf-page__grid2"
          style={{
            padding: '0 var(--pgut) 72px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
          }}
        >
          <Link className="nextlink" to={route('/networking-events')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Next</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Made In New Jersey
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Our own festival property, not client work.
              </span>
            </div>
            <span className="arw">→</span>
          </Link>
          <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'portfolio.proposal', title: 'Request a proposal' })}
            ><div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Start here</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Request a proposal
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Tell us what you&rsquo;re releasing.
              </span>
            </div>
            <span className="arw">→</span>
          </button>
        </div>
      </div>
    </div>
    <ElfFooter />
  </ElfShell>
  );
};

export default Portfolio;
